import { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Label } from "../../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Badge } from "../../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  Shield,
  Clock,
  Calendar,
  Search,
  FileText,
  CheckCircle2,
  Cloud,
  Database,
  ArrowLeft,
  Loader2
} from "lucide-react";
import { DetailsDialog } from "../Cloud/DetailsDialog";
import { RequestCloudAccessDialogProps, Role } from "./interface";
import { myLocalStorage } from "../../StorageHelper";
import useUserStore from "../../../services/userStore";
import { submitForm } from "./Api/tenantgcpapi";
import { azureSubmitForm, submitK8Form } from "./Api/tenantazureapi";
import { awsSubmitForm } from "./Api/tenantawsapi";
import { buildAwsSelectedPolicies, resolveK8SubjectName, toAwsPrivilegeLevel } from "./Data";
import { cn } from "../../../utils/utils";

// Kubernetes verbs
const k8sVerbs = [
  { id: "get", name: "Get", description: "Read individual resources" },
  { id: "list", name: "List", description: "List multiple resources" },
  { id: "watch", name: "Watch", description: "Watch for resource changes" },
  { id: "create", name: "Create", description: "Create new resources" },
  { id: "update", name: "Update", description: "Update existing resources" },
  { id: "patch", name: "Patch", description: "Partially update resources" },
  { id: "delete", name: "Delete", description: "Delete resources" },
  { id: "deletecollection", name: "Delete Collection", description: "Delete multiple resources" },
];

const dialogContentClass = "w-[calc(100vw-1rem)] max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain custom-scrollbar !animate-none !rounded-2xl sm:w-[calc(100vw-2rem)] sm:!rounded-2xl border border-slate-200 bg-card text-foreground p-6 shadow-2xl dark:border-slate-700";
const dialogIconClass = "w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0";
const dialogFormControlClass =
  "rounded-xl border-slate-200 bg-white text-slate-800 shadow-sm placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-0 dark:border-slate-600 dark:bg-slate-800/70 dark:text-foreground dark:placeholder:text-muted-foreground dark:[color-scheme:dark]";
const dialogSearchInputClass =
  cn("h-11", dialogFormControlClass);
const dialogPanelClass = "rounded-2xl border border-border bg-card shadow-sm";

const selectionCardClass = (isSelected: boolean) =>
  cn(
    "rounded-2xl border p-4 cursor-pointer transition-all duration-200 shadow-sm",
    isSelected
      ? "border-primary bg-primary/5 ring-1 ring-primary/30"
      : "border-border bg-card portal-card-hover"
  );

const selectionCheckClass = (isSelected: boolean) =>
  cn(
    "w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors",
    isSelected
      ? "border-primary bg-primary text-primary-foreground"
      : "border-slate-300 bg-card text-transparent dark:border-slate-600"
  );

const accessTypeCardClass = (isSelected: boolean) =>
  cn(
    "flex items-center justify-center rounded-2xl border p-3 cursor-pointer transition-all duration-200 shadow-sm",
    isSelected
      ? "border-primary bg-primary text-primary-foreground ring-1 ring-primary/30 shadow-md shadow-primary/20"
      : "border-border bg-card text-foreground portal-card-hover"
  );

const getRoleSelectionKey = (role: Role) =>
  String(role.id || role.roleUid || role.roleName || role.name);

const getCurrentSelectedRoles = (selectedRoles: Role[], availableRoles: Role[] = []) =>
  selectedRoles.flatMap((selectedRole) => {
    const currentRole = availableRoles.find(
      (availableRole) => getRoleSelectionKey(availableRole) === getRoleSelectionKey(selectedRole)
    );
    return currentRole ? [currentRole] : [];
  });

export default function RequestCloudAccessDialog({
  open,
  onOpenChange,
  resourceName,
  resourceType,
  resourceTypeLabel,
  availableRoles,
  isRolesLoading = false,
  resource,
  from,
  userId,
  userEmail,
  userName,
  upn,
  displayName,
  clustername,
  type
}: RequestCloudAccessDialogProps) {
  const [accessType, setAccessType] = useState("DYNAMIC");
  const [scheduleAccess, setScheduleAccess] = useState(false);
  const [justification, setJustification] = useState("");
  const [timeMinutes, setTimeMinutes] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<Role[]>([]);
  const [step, setStep] = useState<"roleMode" | "roles" | "verbs" | "request">(resourceType === "kubernetes" ? "roleMode" : "roles");
  const [roleSearch, setRoleSearch] = useState("");
  const [verbSearch, setVerbSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [roleMode, setRoleMode] = useState<"existing" | "custom">("existing");
  const [selectedVerbs, setSelectedVerbs] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const userData = useUserStore((state) => state.user);
  const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");
  const currentSelectedRoles = getCurrentSelectedRoles(selectedRoles, availableRoles);

  const toggleRoleSelect = (role: Role) => {
    setSelectedRoles(prev => {
      const roleKey = getRoleSelectionKey(role);
      const isSelected = prev.some(r => getRoleSelectionKey(r) === roleKey);
      return isSelected
        ? prev.filter(r => getRoleSelectionKey(r) !== roleKey)
        : [...prev, role];
    });
  };

  useEffect(() => {
    setSelectedRoles((previousRoles) => {
      const nextRoles = getCurrentSelectedRoles(previousRoles, availableRoles);
      const unchanged =
        previousRoles.length === nextRoles.length &&
        previousRoles.every((role, index) => role === nextRoles[index]);
      return unchanged ? previousRoles : nextRoles;
    });
  }, [availableRoles]);

  const handleSubmit = async () => {
    const requiresPublishedRole =
      resourceType === "aws" ||
      resourceType === "gcp" ||
      resourceType === "azure" ||
      (resourceType === "kubernetes" && roleMode === "existing");

    if (isSubmitting || (requiresPublishedRole && currentSelectedRoles.length === 0)) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (resourceType === "aws") {
        const matchedPolicies = availableRoles?.filter(
          (policy: Role) => currentSelectedRoles.some(
            (selected) => getRoleSelectionKey(selected) === getRoleSelectionKey(policy)
          ),
        ) ?? [];
        const privilegeLevel = toAwsPrivilegeLevel(accessType);

        const payload = {
          awsUsername: userName,
          accessType: "resources",
          accessARN:
            resource?.arn ||
            resource?.clusterArn ||
            resource?.details?.clusterArn ||
            resource?.id ||
            "",
          accessName: resource.name,
          requestMessage: justification,
          requestStatus: "PENDING",
          privilegeLevel,
          selectedTime: privilegeLevel === "PERMANENT" ? 0 : Number(timeMinutes) || 0,
          tenantName: latestTenant,
          selectedPolicies: buildAwsSelectedPolicies(matchedPolicies, latestTenant),
          type: "aws",
          timingMode: scheduleAccess ? "scheduled" : "generic",
          scheduledStart: scheduleAccess ? startDate : null,
          scheduledEnd: scheduleAccess ? endDate : null,
          accountId: selectedAwsAccount,
        };
        await awsSubmitForm(payload);
      }
      else if (resourceType === "azure") {
        const allPathIds = Array.from(new Set(currentSelectedRoles.flatMap(role => role.path ?? [])));
        let data = resource.details;

        let payload = {
          tenantName: latestTenant,
          subscriptionId: data?.subscriptionId,
          resourceScope:
            from === "vm"
              ? data?.instanceId
              : from === "storage"
                ? data?.azureStorageAccountId
                : from === "database"
                  ? data?.instanceId
                  : from === "kubernetes"
                    ? data?.azureId
                    : null,
          roleDefinitionPathIds: allPathIds,
          principleId: userId,
          principleType: "USER",
          expiryTimeAmount: timeMinutes,
          description: justification,
          userEmail: userData.email,
          timingMode: "",
          privilegeLevel: accessType.toUpperCase(),
        }
        await azureSubmitForm(payload);
      } else if (resourceType === "gcp") {
        let data = resource.details;
        const matchedRoles = availableRoles?.filter(
          (r: Role) => currentSelectedRoles.some(
            (selected) => getRoleSelectionKey(selected) === getRoleSelectionKey(r)
          )
        );


        const roleDetails = matchedRoles?.flatMap((r: any) => r.details || []) || [];
        const payload = {
          projectId: data?.projectId || resource?.project || "",
          email: userData?.email || "",
          gcpUserEmail: userEmail || "",
          accessType: "resources",
          roles: roleDetails,
          requestMessage: justification,
          requestStatus: "PENDING",
          privilegeLevel: accessType.toUpperCase(),
          selectedTime: timeMinutes,
          tenantName: latestTenant,
          accessName: resource?.name || resourceName,
          resourceName: resourceName,
          gcpResourceType: resource?.type === "vm" ? "VIRTUAL_MACHINE" : resource?.type === "database" ? "DATABASE" : "STORAGE_BUCKET",
          timingMode: "",
        };
        await submitForm(payload);
      } else if (resourceType === "kubernetes") {
        let data = resource;
        const cloudProviderType = String(data.cloudProviderType || "").toLowerCase();
        let subjectName = upn?.trim() || "";
        let subjectDisplayName = displayName?.trim() || "";

        if (!subjectName) {
          const resolved = await resolveK8SubjectName(cloudProviderType, userData.email);
          subjectName = resolved.upn?.trim() || "";
          subjectDisplayName = resolved.displayName?.trim() || subjectDisplayName;
        }

        if (!subjectName) {
          window.alert("Configure your cloud user identity before requesting Kubernetes access.");
          return;
        }

        const payload = roleMode === "custom"
          ? {
            verbs: selectedVerbs ? selectedVerbs : ["get"],
            resourcesName: data.name,
            k8ResourceType: type,
            k8ResourceId: data.uid,
            k8ResourceName: data.name,
            namespace: data.namespace,
            userName: subjectName,
            expiryTimeAmount: timeMinutes,
            wsUserEmail: userData.email,
            message: justification,
            clusterId: data.clusterId,
            cloudResourceAccountId: data.cloudResourceAccountId,
            cloudType: data.cloudProviderType,
            wsTenantName: latestTenant,
            user: subjectDisplayName,
            clusterName: clustername,
            timingMode: "",
            privilegeLevel: accessType.toUpperCase(),
          }
          : {
            roleId: currentSelectedRoles[0]?.roleUid,
            roleName: currentSelectedRoles[0]?.roleName,
            roleKind: currentSelectedRoles[0]?.roleKind,
            resourcesName: data.name,
            k8ResourceType: type,
            k8ResourceId: data.uid,
            k8ResourceName: data.name,
            namespace: data.namespace,
            userName: subjectName,
            expiryTimeAmount: timeMinutes,
            wsUserEmail: userData.email,
            message: justification,
            clusterId: data.clusterId,
            cloudResourceAccountId: data.cloudResourceAccountId,
            cloudType: data.cloudProviderType,
            wsTenantName: latestTenant,
            user: subjectDisplayName,
            clusterName: clustername,
            timingMode: "",
            privilegeLevel: accessType.toUpperCase(),
          };

        console.log(payload)
        await submitK8Form(payload)
      }

      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (step === "request") {
      if (resourceType === "kubernetes" && roleMode === "custom") {
        setStep("verbs");
      } else {
        setStep(resourceType === "kubernetes" ? "roleMode" : "roles");
      }
    } else if (step === "verbs") {
      setStep("roleMode");
    } else if (step === "roles") {
      if (resourceType === "kubernetes") {
        setStep("roleMode");
      }
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    // Reset form
    setAccessType("DYNAMIC");
    setScheduleAccess(false);
    setJustification("");
    setTimeMinutes("");
    setSelectedRoles([]);
    setStep(resourceType === "kubernetes" ? "roleMode" : "roles");
    setRoleSearch("");
    setVerbSearch("");
    setStartDate("");
    setEndDate("");
    setRoleMode("existing");
    setSelectedVerbs([]);
  };

  // Reset form when dialog closes OR when resourceType changes
  useEffect(() => {
    if (!open) {
      setAccessType("DYNAMIC");
      setScheduleAccess(false);
      setJustification("");
      setTimeMinutes("");
      setSelectedRoles([]);
      setStep(resourceType === "kubernetes" ? "roleMode" : "roles");
      setRoleSearch("");
      setVerbSearch("");
      setStartDate("");
      setEndDate("");
      setRoleMode("existing");
      setSelectedVerbs([]);
    }
  }, [open]);

  // Reset when resourceType changes (switching between kubernetes/cloud resources)
  useEffect(() => {
    setStep(resourceType === "kubernetes" ? "roleMode" : "roles");
    setSelectedRoles([]);
    setRoleMode("existing");
    setSelectedVerbs([]);
  }, [resourceType]);


  const awsPolicyMode = resourceType === "aws";
  const selectionLabel = awsPolicyMode ? "Policy" : "Role";
  const selectionLabelPlural = awsPolicyMode ? "Policies" : "Roles";
  const rolesToUse = availableRoles;
  const filteredRoles = rolesToUse?.filter(role =>
    role.name.toLowerCase().includes(roleSearch.toLowerCase()) ||
    (role.description && role.description.toLowerCase().includes(roleSearch.toLowerCase()))
  );

  // Filter verbs based on search
  const filteredVerbs = k8sVerbs.filter(verb =>
    verb.name.toLowerCase().includes(verbSearch.toLowerCase()) ||
    verb.description.toLowerCase().includes(verbSearch.toLowerCase())
  );

  const toggleVerb = (verbId: string) => {
    setSelectedVerbs(prev =>
      prev.includes(verbId)
        ? prev.filter(v => v !== verbId)
        : [...prev, verbId]
    );
  };

  // Step 1: Choose between existing role or custom role (Kubernetes only)
  if (step === "roleMode") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={dialogContentClass}>
          <DialogHeader className="space-y-4">
            <div className="flex items-center gap-3">
              <div className={dialogIconClass}>
                <Database className="w-5 h-5 text-primary" />
              </div>
              <div className="min-w-0 pr-6">
                <DialogTitle className="text-xl font-semibold">Select Roles</DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Choose one or more roles to request access to <span className="font-medium text-foreground">{resourceName}</span>
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <Card className={dialogPanelClass}>
              <CardContent className="p-4 space-y-3">
                <div
                  className={selectionCardClass(roleMode === "existing")}
                  onClick={() => setRoleMode("existing")}
                  data-testid="option-existing-role"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-foreground">Select Existing Role</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Choose from pre-configured roles with predefined permissions
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className={selectionCardClass(roleMode === "custom")}
                  onClick={() => setRoleMode("custom")}
                  data-testid="option-custom-role"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-foreground">Use Custom Role</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Create a custom role by selecting specific Kubernetes verbs
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={handleCancel} data-testid="button-cancel" className="px-6">
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (roleMode === "existing") {
                    setStep("roles");
                  } else {
                    setStep("verbs");
                  }
                }}
                data-testid="button-next"
                className="px-6"
              >
                Next
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Step 2a: Select Kubernetes verbs for custom role
  if (step === "verbs") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={dialogContentClass}>
          <DialogHeader className="space-y-4">
            <div className="flex items-center gap-3">
              <div className={dialogIconClass}>
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <div className="min-w-0 pr-6">
                <DialogTitle className="text-xl font-semibold">Select Verbs</DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Choose Kubernetes verbs for <span className="font-medium text-foreground">{resourceName}</span>
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            {/* Search Section */}
            <Card className={dialogPanelClass}>
              <CardContent className="p-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search verbs by name or description..."
                    value={verbSearch}
                    onChange={(e) => setVerbSearch(e.target.value)}
                    className={cn("pl-10 text-sm", dialogSearchInputClass)}
                    data-testid="input-verb-search"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className={dialogPanelClass}>
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-foreground">
                  <Shield className="w-4 h-4 text-primary" />
                  Kubernetes Verbs ({selectedVerbs.length} selected)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-2">
                <div className="space-y-2 max-h-80 overflow-y-auto px-1 py-1">
                  {filteredVerbs.map((verb) => {
                    const isSelected = selectedVerbs.includes(verb.id);
                    return (
                      <div
                        key={verb.id}
                        className={selectionCardClass(isSelected)}
                        onClick={() => toggleVerb(verb.id)}
                        data-testid={`verb-${verb.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={selectionCheckClass(isSelected)}>
                            {isSelected && (
                              <CheckCircle2 className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm text-foreground">{verb.name}</div>
                            <div className="text-xs text-muted-foreground">{verb.description}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {filteredVerbs.length === 0 && (
                  <div className="text-center py-8">
                    <Search className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">
                      No verbs found matching your search.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Try adjusting your search criteria.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-between gap-3 pt-4">
              <Button variant="outline" onClick={handleBack} data-testid="button-back" className="px-6">
                Back
              </Button>
              <Button
                onClick={() => setStep("request")}
                disabled={selectedVerbs.length === 0}
                data-testid="button-next"
                className="px-6"
              >
                Next
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Step 2b: Show role selection step (for existing roles)
  if (step === "roles") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={dialogContentClass}>
          <DialogHeader className="space-y-4">
            <div className="flex items-center gap-3">
              <div className={dialogIconClass}>
                {resourceType === "gcp" ? (
                  <Cloud className="w-5 h-5 text-primary" />
                ) : (
                  <Database className="w-5 h-5 text-primary" />
                )}
              </div>
              <div className="min-w-0 pr-6">
                <DialogTitle className="text-xl font-semibold">Select {selectionLabel}</DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Choose {awsPolicyMode ? "policies" : "a role"} to request access to <span className="font-medium text-foreground">{resourceName}</span>
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            {/* Search Section */}
            <Card className={dialogPanelClass}>
              <CardContent className="p-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={`Search ${selectionLabelPlural.toLowerCase()} by name or description...`}
                    value={roleSearch}
                    onChange={(e) => setRoleSearch(e.target.value)}
                    disabled={isRolesLoading}
                    className={cn("pl-10 text-sm", dialogSearchInputClass)}
                    data-testid="input-role-search"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Roles List */}
            <Card className={dialogPanelClass}>
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-foreground">
                  {isRolesLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                  ) : (
                    <Shield className="w-4 h-4 text-primary" />
                  )}
                  {isRolesLoading
                    ? `Loading ${selectionLabelPlural.toLowerCase()}...`
                    : `Available ${selectionLabelPlural} (${filteredRoles?.length || 0})`}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-2">
                <div className="h-48 space-y-3 overflow-y-auto px-1 py-1">
                  {isRolesLoading && (
                    <div className="flex h-full flex-col items-center justify-center gap-3 text-muted-foreground">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      <p className="text-sm">
                        Loading available {selectionLabelPlural.toLowerCase()}...
                      </p>
                    </div>
                  )}
                  {!isRolesLoading && filteredRoles?.length === 0 && (
                    <div className="flex h-full flex-col items-center justify-center px-4 text-center text-muted-foreground">
                      <Search className="mb-3 h-8 w-8" />
                      {rolesToUse?.length ? (
                        <>
                          <p className="text-sm">No {selectionLabelPlural.toLowerCase()} match your search.</p>
                          <p className="mt-1 text-xs">Try adjusting your search criteria.</p>
                        </>
                      ) : awsPolicyMode ? (
                        <p className="text-sm">
                          No published policies are available. Ask your admin to publish policies under AWS Roles → Policies.
                        </p>
                      ) : (
                        <p className="text-sm">
                          No available {selectionLabelPlural.toLowerCase()} were returned for this resource.
                        </p>
                      )}
                    </div>
                  )}
                  {!isRolesLoading && filteredRoles?.map((role) => {
                    // Prepare role details for DetailsDialog
                    const roleDetails: any = {
                      name: role.name,
                      description: role.description || "No description",
                    };

                    if (resourceType === "kubernetes") {
                      roleDetails.verbs = role.verbs || [];
                      roleDetails.resources = role.resources || [];
                      roleDetails.apiGroups = role.apiGroups || [];
                    } else if (resourceType === "gcp") {
                      roleDetails.permissions = role.permissions || [];
                    } else if (resourceType === "aws") {
                      roleDetails.permissions = role.permissions || [];
                    } else if (resourceType === "azure") {
                      roleDetails.permissions = role.permissions || [];
                    }
                    const isSelected = currentSelectedRoles.some(
                      (selectedRole) =>
                        getRoleSelectionKey(selectedRole) === getRoleSelectionKey(role)
                    );
                    return (
                      <div
                        key={role.id}
                        className={cn(selectionCardClass(isSelected), "group")}
                        onClick={() => toggleRoleSelect(role)}
                        data-testid={`role-${role.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <div className={selectionCheckClass(isSelected)}>
                              {isSelected && (
                                <CheckCircle2 className="w-3 h-3 text-white" />
                              )}
                            </div>
                            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center group-hover:scale-105 transition-transform flex-shrink-0">
                              <Shield className="w-5 h-5 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-foreground max-w-full break-words break-all whitespace-normal">
                                {role.name}
                              </div>
                              {role.description && (
                                <div className="text-xs text-muted-foreground break-words break-all whitespace-normal">
                                  {role.description}
                                </div>
                              )}
                            </div>


                          </div>
                          <div onClick={(e) => e.stopPropagation()}>
                            <DetailsDialog
                              data={role}
                              title={`${role.name} Role Details`}
                              trigger="icon"
                              testId={`button-role-info-${role.id}`}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between gap-3 pt-4">
              {resourceType === "kubernetes" ? (
                <Button variant="outline" onClick={handleBack} data-testid="button-back" className="px-6">
                  Back
                </Button>
              ) : (
                <Button variant="outline" onClick={handleCancel} data-testid="button-cancel" className="px-6">
                  Cancel
                </Button>
              )}
              <div />
              <Button
                onClick={() => setStep("request")}
                disabled={currentSelectedRoles.length === 0}
                data-testid="button-next"
                className="px-6"
              >
                Next
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Main request form
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={dialogContentClass}>
        <DialogHeader className="space-y-2">
          <div className="flex items-center gap-3">
            <div className={dialogIconClass}>
              {resourceType === "gcp" ? (
                <Cloud className="w-5 h-5 text-primary" />
              ) : (
                <Database className="w-5 h-5 text-primary" />
              )}
            </div>
            <div className="min-w-0 pr-6">
              <DialogTitle className="text-xl font-semibold">
                Request {resourceType === "gcp" ? "GCP" : resourceType === "aws" ? "AWS" : resourceType === "azure" ? "Azure" : "Kubernetes"} Access
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                Configure access for <span className="font-medium text-foreground">{resourceName}</span>
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Access Type */}
          <div>
            <Label className="text-sm font-medium mb-3 block flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Access Type
            </Label>
            <RadioGroup value={accessType} onValueChange={setAccessType} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="relative">
                <RadioGroupItem value="DYNAMIC" id="DYNAMIC" className="peer sr-only" />
                <Label
                  htmlFor="DYNAMIC"
                  className={accessTypeCardClass(accessType === "DYNAMIC")}
                >
                  <div className="flex items-center gap-2">
                    <Clock className={cn("w-4 h-4", accessType === "DYNAMIC" ? "text-primary-foreground" : "text-orange-600 dark:text-orange-400")} />
                    <span className="font-medium text-sm">Timed</span>
                  </div>
                </Label>
              </div>
              <div className="relative">
                <RadioGroupItem value="PERMANENT" id="permanent" className="peer sr-only" />
                <Label
                  htmlFor="permanent"
                  className={accessTypeCardClass(accessType === "PERMANENT")}
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className={cn("w-4 h-4", accessType === "PERMANENT" ? "text-primary-foreground" : "text-green-600 dark:text-green-400")} />
                    <span className="font-medium text-sm">Permanent</span>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Request Details */}
          <Card className={dialogPanelClass}>
            <CardContent className="p-4 space-y-3">
              <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 sm:gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Resource</Label>
                  <div className="font-medium text-foreground break-all">{resourceName}</div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Type</Label>
                  <div className="font-medium text-foreground">{resourceTypeLabel || (resourceType === "gcp" ? "GCP" : "Kubernetes")}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {(currentSelectedRoles.length > 0 || (roleMode === "custom" && selectedVerbs.length > 0)) && (
            <Card className={dialogPanelClass}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-foreground">
                    {currentSelectedRoles.length > 0
                      ? `Selected ${currentSelectedRoles.length > 1 ? selectionLabelPlural : selectionLabel}`
                      : "Selected Verbs"}
                  </span>
                </div>

                {currentSelectedRoles.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {currentSelectedRoles.map(r => (
                        <Badge key={r.id} variant="secondary" className="max-w-full rounded-md border border-primary/20 bg-primary/10 text-primary text-xs font-medium leading-relaxed whitespace-normal break-words text-left">
                          {r?.name}
                        </Badge>
                      ))}
                  </div>
                )}

                {roleMode === "custom" && selectedVerbs.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedVerbs.map(verbId => {
                      const verb = k8sVerbs.find(v => v.id === verbId);
                      return (
                        <Badge key={verbId} variant="secondary" className="max-w-full rounded-md border border-primary/20 bg-primary/10 text-primary text-xs font-medium leading-relaxed whitespace-normal break-words text-left">
                          {verb?.name}
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Schedule Access  
          TODO: Backend need to handle for all cloud with Kubernetes
          <div className="flex items-center justify-between">
            <Label htmlFor="schedule-access" className="text-sm font-medium cursor-pointer">Schedule Access</Label>
            <Switch
              id="schedule-access"
              checked={scheduleAccess}
              onCheckedChange={setScheduleAccess}
            />
          </div>
          */}
          {/* Justification and Configuration */}
          <Card className={dialogPanelClass}>
            <CardContent className="p-4 space-y-3">
              {/* Justification */}
              <div>
                <Label htmlFor="justification" className="text-sm font-medium mb-2 block flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Access Justification *
                </Label>
                <Textarea
                  id="justification"
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  placeholder="Provide business justification for this access request..."
                  className={cn("min-h-[88px] resize-none", dialogFormControlClass)}
                  data-testid="textarea-justification"
                />
              </div>

              {/* Time Input */}
              {accessType === "DYNAMIC" && (
                <div>
                  <Label htmlFor="time-minutes" className="text-sm font-medium mb-2 block flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Duration (minutes) *
                  </Label>
                  <Input
                    id="time-minutes"
                    value={timeMinutes}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") {
                        setTimeMinutes("");
                        return;
                      }
                      if (!/^\d+$/.test(value)) return;
                      const num = Number(value);
                      if (num >= 1 && num <= 999) {
                        setTimeMinutes(value);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-", "."].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    placeholder="e.g., 60"
                    type="number"
                    min="1"
                    max="999"
                    className={cn("max-w-xs", dialogSearchInputClass)}
                    data-testid="input-time-minutes"
                  />
                </div>
              )}

              {/* Schedule Access Date Fields */}
              {scheduleAccess && (
                <div>
                  <Label className="text-sm font-medium mb-2 block flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Schedule Window *
                  </Label>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="start-date" className="text-xs text-muted-foreground mb-1 block">
                        Start
                      </Label>
                      <Input
                        id="start-date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        type="datetime-local"
                        className={cn(dialogSearchInputClass, "dark:[color-scheme:dark]")}
                        data-testid="input-start-date"
                      />
                    </div>
                    <div>
                      <Label htmlFor="end-date" className="text-xs text-muted-foreground mb-1 block">
                        End
                      </Label>
                      <Input
                        id="end-date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        type="datetime-local"
                        className={cn(dialogSearchInputClass, "dark:[color-scheme:dark]")}
                        data-testid="input-end-date"
                      />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 pt-3 border-t border-border sm:flex-row sm:items-center sm:justify-between">
            <Button variant="outline" onClick={handleBack} data-testid="button-back" className="w-full sm:w-auto">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={handleCancel} data-testid="button-cancel" className="w-full sm:w-auto">
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  !justification.trim() ||
                  isSubmitting ||
                  (accessType === "DYNAMIC" && !timeMinutes) ||
                  (scheduleAccess && (!startDate || !endDate)) ||
                  ((resourceType === "aws" || resourceType === "gcp" || resourceType === "azure") && currentSelectedRoles.length === 0) ||
                  (resourceType === "kubernetes" && roleMode === "existing" && currentSelectedRoles.length === 0)
                }
                data-testid="button-submit"
                className="w-full bg-primary hover:bg-primary/90 sm:w-auto"
              >
                <Shield className="w-4 h-4 mr-2" />
                Submit
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
